import { Share2, Copy, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useShare } from '@/hooks/use-share'
import { cn } from '@/lib/utils'

interface ShareButtonProps {
  url: string
  title: string
  description?: string
  variant?: 'icon' | 'button' | 'compact'
  className?: string
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 1.092.07 1.594.14v3.28c-.48-.05-.84-.07-1.22-.07-1.73 0-2.4.656-2.4 2.363v1.845h3.45l-.592 3.667h-2.858v8.073C18.395 22.538 22 18.18 22 12.044c0-5.518-4.477-9.991-9.998-9.991C6.48 2.053 2 6.525 2 12.044c0 5.149 3.891 9.394 8.901 9.95z" />
    </svg>
  )
}

export function ShareButton({ url, title, description, variant = 'button', className }: ShareButtonProps) {
  const { share, copyLink, canNativeShare, shareOnX, shareOnFacebook, shareViaEmail } = useShare({
    url,
    title,
    description,
  })

  // Native share — single button, no dropdown needed
  if (canNativeShare) {
    if (variant === 'icon') {
      return (
        <button
          type="button"
          onClick={share}
          className={cn(
            'inline-flex items-center justify-center rounded-full p-2 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700',
            className
          )}
          aria-label="Share"
        >
          <Share2 className="size-4" />
        </button>
      )
    }

    return (
      <Button
        type="button"
        variant="outline"
        size={variant === 'compact' ? 'sm' : 'default'}
        onClick={share}
        className={cn('gap-2', className)}
      >
        <Share2 className="size-4" />
        {variant !== 'compact' && 'Share'}
      </Button>
    )
  }

  // Fallback dropdown for browsers without Web Share API
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === 'icon' ? (
          <button
            type="button"
            className={cn(
              'inline-flex items-center justify-center rounded-full p-2 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700',
              className
            )}
            aria-label="Share"
          >
            <Share2 className="size-4" />
          </button>
        ) : (
          <Button
            type="button"
            variant="outline"
            size={variant === 'compact' ? 'sm' : 'default'}
            className={cn('gap-2', className)}
          >
            <Share2 className="size-4" />
            {variant !== 'compact' && 'Share'}
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={copyLink} className="gap-2">
          <Copy className="size-4" />
          Copy Link
        </DropdownMenuItem>
        <DropdownMenuItem onClick={shareOnX} className="gap-2">
          <XIcon className="size-4" />
          Share on X
        </DropdownMenuItem>
        <DropdownMenuItem onClick={shareOnFacebook} className="gap-2">
          <FacebookIcon className="size-4" />
          Share on Facebook
        </DropdownMenuItem>
        <DropdownMenuItem onClick={shareViaEmail} className="gap-2">
          <Mail className="size-4" />
          Email
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
